import type { ManualPlacementConfig, PlacementConfig, PublisherPlatform } from '../../types/metaAdDraft';
import {
  PLACEMENT_DEPENDENCIES,
  defaultCommonConfig,
  positionArrayKey,
  positionsForPlatform,
} from '../../lib/placementMatrix';

// Stage 7A D3.1 — pure mutation helpers for the manual placement matrix. Every
// mutation re-derives publisherPlatforms (a platform is "on" iff it has ≥1
// position) so the config is always validator-consistent.

const EMPTY_MANUAL: ManualPlacementConfig = {
  publisherPlatforms: [],
  facebookPositions: [],
  instagramPositions: [],
  messengerPositions: [],
  audienceNetworkPositions: [],
};

// Mode switch. automatic → drop the manual config. manual → start from the
// "Common set" (FB + IG Feed/Story/Reels) so the AM has a sensible default (D3.1).
export function setPlacementMode(mode: 'automatic' | 'manual'): PlacementConfig {
  return mode === 'automatic'
    ? { mode: 'automatic', manualConfig: null }
    : { mode: 'manual', manualConfig: defaultCommonConfig() };
}

// Re-derive publisherPlatforms from the four position arrays.
function derivePlatforms(cfg: ManualPlacementConfig): PublisherPlatform[] {
  const out: PublisherPlatform[] = [];
  if (cfg.facebookPositions.length) out.push('facebook');
  if (cfg.instagramPositions.length) out.push('instagram');
  if (cfg.messengerPositions.length) out.push('messenger');
  if (cfg.audienceNetworkPositions.length) out.push('audience_network');
  return out;
}

function withPlatforms(cfg: ManualPlacementConfig): ManualPlacementConfig {
  return { ...cfg, publisherPlatforms: derivePlatforms(cfg) };
}

// Toggle a single position, applying Meta co-selection dependencies (PART E):
// - enabling a position auto-enables its dependencies (e.g. explore_home → explore)
// - disabling a position auto-disables anything that depends on it
// Returns the new config + an optional inline message describing an auto-pair.
export function togglePosition(
  cfg: ManualPlacementConfig,
  platform: PublisherPlatform,
  value: string,
  on: boolean,
): { cfg: ManualPlacementConfig; message?: string } {
  const key = positionArrayKey(platform);
  const set = new Set(cfg[key] as string[]);
  let message: string | undefined;

  if (on) {
    set.add(value);
    for (const dep of PLACEMENT_DEPENDENCIES[`${platform}:${value}`] ?? []) {
      const [dp, dv] = dep.split(':');
      if (dp === platform && dv && !set.has(dv)) {
        set.add(dv);
        message = 'Auto-paired with Explore (Meta requires both).';
      }
    }
  } else {
    set.delete(value);
    for (const [k, deps] of Object.entries(PLACEMENT_DEPENDENCIES)) {
      if (!deps.includes(`${platform}:${value}`)) continue;
      const [kp, kv] = k.split(':');
      if (kp === platform && kv && set.has(kv)) {
        set.delete(kv);
        message = 'Removed Explore Home because it requires Explore.';
      }
    }
  }

  const next = withPlatforms({ ...cfg, [key]: Array.from(set) });
  return { cfg: next, message };
}

// Toggle an entire platform: on → check ALL of its positions; off → clear them.
export function togglePlatform(
  cfg: ManualPlacementConfig,
  platform: PublisherPlatform,
  on: boolean,
): ManualPlacementConfig {
  const key = positionArrayKey(platform);
  const all = on ? positionsForPlatform(platform).map((p) => p.value) : [];
  return withPlatforms({ ...cfg, [key]: all });
}

export { EMPTY_MANUAL };
